import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface XSpace {
  id: string;
  event_id: number;
  title: string;
  title_ar: string | null;
  x_space_url: string | null;
  scheduled_at: string;
  created_at: string;
  updated_at: string;
  reminder_30min_sent: boolean;
  reminder_10min_sent: boolean;
  image_url: string | null;
  description: string | null;
  description_ar: string | null;
  end_time: string | null;
  attendees: number | null;
  is_live: boolean | null;
}

export interface FormattedXSpace {
  id: number;
  dbId: string; // UUID from database for routing
  title: { en: string; ar: string };
  date: { en: string; ar: string };
  startTime: { en: string; ar: string };
  endTime: { en: string; ar: string } | null;
  image: string;
  xSpaceUrl: string | null;
  category: string;
  isLive: boolean;
  isEnded: boolean;
  attendees: number;
}

const formatDate = (dateStr: string): { en: string; ar: string } => {
  const date = new Date(dateStr);
  const options: Intl.DateTimeFormatOptions = { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  };
  
  return {
    en: date.toLocaleDateString('en-US', options),
    ar: date.toLocaleDateString('ar-SA', options)
  };
};

const formatTime = (dateStr: string): { en: string; ar: string } => {
  const date = new Date(dateStr);
  const options: Intl.DateTimeFormatOptions = { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true
  };
  
  return {
    en: date.toLocaleTimeString('en-US', options),
    ar: date.toLocaleTimeString('ar-SA', options)
  };
};

const isSpaceLive = (scheduledAt: string, endTime: string | null, isLiveDb: boolean | null): boolean => {
  // If explicitly marked as live in database, use that
  if (isLiveDb === true) return true;
  if (isLiveDb === false) return false;
  
  const now = new Date();
  const scheduled = new Date(scheduledAt);
  const diffMs = now.getTime() - scheduled.getTime();
  
  // If there's an end time and we're past it, not live
  if (endTime) {
    const end = new Date(endTime);
    if (now > end) return false;
  }
  
  // Consider live if within 2 hours after scheduled time
  return diffMs >= 0 && diffMs < 2 * 60 * 60 * 1000;
};

const isSpaceEnded = (scheduledAt: string, endTime: string | null, isLiveDb: boolean | null): boolean => {
  const now = new Date();
  const scheduled = new Date(scheduledAt);
  
  // If explicitly marked as live, not ended
  if (isLiveDb === true) return false;
  
  // If there's an end time and we're past it, it's ended
  if (endTime) {
    const end = new Date(endTime);
    if (now > end) return true;
  }
  
  // If scheduled time is in the past and more than 2 hours have passed, it's ended
  const diffMs = now.getTime() - scheduled.getTime();
  return diffMs > 2 * 60 * 60 * 1000;
};

export const useXSpaces = (limit?: number) => {
  return useQuery({
    queryKey: ["x-spaces", limit],
    queryFn: async (): Promise<FormattedXSpace[]> => {
      let query = supabase
        .from("scheduled_x_spaces")
        .select("*")
        .order("created_at", { ascending: true });
      
      if (limit) {
        query = query.limit(limit);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching X Spaces:", error);
        throw error;
      }

      if (!data || data.length === 0) {
        return [];
      }

      return data.map((space: XSpace): FormattedXSpace => ({
        id: space.event_id,
        dbId: space.id,
        title: {
          en: space.title,
          ar: space.title_ar || space.title
        },
        date: formatDate(space.scheduled_at),
        startTime: formatTime(space.scheduled_at),
        endTime: null,
        image: space.image_url || "/images/x-spaces-default.jpg",
        xSpaceUrl: space.x_space_url,
        category: "XSpaces",
        isLive: isSpaceLive(space.scheduled_at, space.end_time, space.is_live),
        isEnded: isSpaceEnded(space.scheduled_at, space.end_time, space.is_live),
        attendees: space.attendees || 0
      }));
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useUpcomingXSpaces = () => {
  return useQuery({
    queryKey: ["x-spaces-upcoming"],
    queryFn: async (): Promise<FormattedXSpace[]> => {
      const now = new Date().toISOString();
      
      const { data, error } = await supabase
        .from("scheduled_x_spaces")
        .select("*")
        .gte("scheduled_at", now)
        .order("scheduled_at", { ascending: true });

      if (error) {
        console.error("Error fetching upcoming X Spaces:", error);
        throw error;
      }

      if (!data || data.length === 0) {
        return [];
      }

      return data.map((space: XSpace): FormattedXSpace => ({
        id: space.event_id,
        dbId: space.id,
        title: {
          en: space.title,
          ar: space.title_ar || space.title
        },
        date: formatDate(space.scheduled_at),
        startTime: formatTime(space.scheduled_at),
        endTime: null,
        image: space.image_url || "/images/x-spaces-default.jpg",
        xSpaceUrl: space.x_space_url,
        category: "XSpaces",
        isLive: isSpaceLive(space.scheduled_at, space.end_time, space.is_live),
        isEnded: isSpaceEnded(space.scheduled_at, space.end_time, space.is_live),
        attendees: space.attendees || 0
      }));
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};
